import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { useGlobalState } from '../../contexts/GlobalStateContext';
import { useProjects } from '../../contexts/ProjectContext';
import { usePostModal } from '../../contexts/PostModalContext';
import { useHistory } from 'react-router-dom';
import {
  CheckCircle2,
  Plus,
  ArrowUpRight,
  AlertCircle,
  BarChart3,
  Share2,
  Bookmark,
  X,
  Star,
  PlusCircle,
  Check,
  Pencil
} from 'lucide-react';
import { getPlatformIcon, getPlatformColor } from '../../utils/platformIcons';
import { cn } from "../../lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../ui/tooltip";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import { Button } from "../ui/button";
import { Separator } from "../ui/separator";
import { ScrollArea } from "../ui/scroll-area";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import { usePresets } from '../../hooks/usePresets';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { API_BASE_URL } from '../../config';

import SocialConnectionModal from '../SocialConnectionModal';
import SocialAvatar from '../SocialAvatar';

interface Account {
  id: string;
  name: string;
  platform: string;
  handle?: string;
  avatarUrl?: string | null;
  isValid?: boolean;
  status?: 'PENDING' | 'PROCESSING' | 'COMPLETED' | 'FAILED' | 'DELETED';
}

interface SocialMediaSelectorProps {
  accounts?: Account[];
  selectedIds?: string[];
  onToggle?: (id: string) => void;
  showConnectButton?: boolean;
  showPresetsButton?: boolean;
  showAggregateButton?: boolean;
  isAggregateSelected?: boolean;
  onAggregateClick?: () => void;
  className?: string;
  mode?: 'toggle' | 'select';
  disabledAccountIds?: string[];
  disabledReason?: string;
}

function SortableAccount({
  account,
  isSelected,
  isSmall,
  iconSize,
  badgeSize,
  badgeIconSize,
  onClick,
  isDisabled,
  disabledReason,
}: {
  account: Account;
  isSelected: boolean;
  isSmall: boolean;
  iconSize: string;
  badgeSize: string;
  badgeIconSize: string;
  onClick: () => void;
  isDisabled?: boolean;
  disabledReason?: string;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: account.id });

  const style = {
    transform: transform ? `translate3d(0, ${transform.y}px, 0)` : undefined,
    transition: isDragging ? undefined : transition,
    zIndex: isDragging ? 50 : undefined,
    opacity: isDragging ? 0.6 : 1,
  };

  const platformColor = getPlatformColor(account.platform);

  return (
    <div ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={cn("relative group", isDisabled ? "cursor-not-allowed" : "cursor-pointer")}
            onClick={isDisabled ? undefined : onClick}
          >
            <div className={cn(
              "p-1 rounded-full transition-all duration-300 transform relative",
              isDisabled ? "ring-0 opacity-30" : (isSelected ? "ring-2 ring-offset-2 ring-offset-[#1e293b] group-hover:scale-110" : "ring-0 group-hover:scale-110")
            )} style={{ borderColor: isSelected && !isDisabled ? platformColor : 'transparent' }}>
              <div className={cn(
                "border border-border transition-all duration-300 rounded-full overflow-hidden",
                iconSize,
                isDisabled ? "grayscale opacity-30" : (isSelected ? "grayscale-0 opacity-100" : "grayscale opacity-20 group-hover:grayscale-0 group-hover:opacity-100"),
                (!account.isValid || account.status === 'DELETED') && "border-red-500 opacity-40 grayscale"
              )}>
                <SocialAvatar
                  accountId={account.id}
                  platform={account.platform}
                  name={account.name}
                  avatarUrl={account.avatarUrl}
                  imgClassName={cn(!isSelected && "filter grayscale")}
                  iconClassName="h-4 w-4"
                />
              </div>

              {(account.isValid === false || account.status === 'DELETED') && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <AlertCircle className={cn("text-red-500 drop-shadow-md", isSmall ? "h-3 w-3" : "h-5 w-5")} />
                </div>
              )}
            </div>

            {/* Platform Badge */}
            <div
              className={cn(
                "absolute -bottom-1 -right-1 rounded-full border flex items-center justify-center shadow-lg transition-all duration-300 group-hover:rotate-12",
                badgeSize,
                isSelected
                  ? "grayscale-0 opacity-100 scale-110"
                  : "bg-card grayscale opacity-40 group-hover:grayscale-0 group-hover:opacity-100"
              )}
              style={{
                backgroundColor: isSelected ? platformColor : '#1e293b',
                borderColor: isSelected ? 'white' : platformColor,
                color: isSelected ? 'white' : platformColor
              }}
            >
              {getPlatformIcon(account.platform, badgeIconSize)}
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent side="right" className="bg-background border-border text-foreground">
          <div className="flex flex-col gap-1">
            <p className="font-bold flex items-center gap-2">
              {account.name}
              {(account.isValid === false || account.status === 'DELETED') && <AlertCircle className="h-3 w-3 text-red-500" />}
            </p>
            <p className="text-[10px] text-muted-foreground capitalize">
              {account.handle ? `${account.handle.startsWith('@') ? account.handle : `@${account.handle}`} • ` : ''}{account.platform}
            </p>
            {account.isValid === false && (
              <p className="text-[9px] text-red-400 font-medium italic max-w-[150px]">
                Connection expired. Click to reconnect.
              </p>
            )}
            {account.status === 'DELETED' && (
              <p className="text-[9px] text-red-400 font-medium italic max-w-[150px]">
                This post has been deleted from this network.
              </p>
            )}
            {isDisabled && disabledReason && (
              <p className="text-[9px] text-amber-400 font-medium italic max-w-[150px]">
                {disabledReason}
              </p>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </div>
  );
}

export default function SocialMediaSelector({
  accounts,
  selectedIds,
  onToggle,
  showConnectButton = true,
  showPresetsButton = true,
  showAggregateButton = false,
  isAggregateSelected = false,
  onAggregateClick,
  className,
  mode = 'toggle',
  disabledAccountIds,
  disabledReason
}: SocialMediaSelectorProps): JSX.Element {
  const { localStorage: globalState, updateLocalStorage } = useGlobalState();
  const { activeProject, refreshProjects } = useProjects();
  const { closePostModal } = usePostModal();
  const history = useHistory();
  const token = window.localStorage.getItem('token');

  const internalSelectedPlatforms = globalState.selectedPlatforms || [];
  const currentSelectedIds = selectedIds || internalSelectedPlatforms;

  // Cleanup selectedPlatforms to only include valid IDs for the active project
  useEffect(() => {
    if (!selectedIds && activeProject?.socialAccounts && internalSelectedPlatforms.length > 0) {
      const validIds = activeProject.socialAccounts.map(sa => sa.socialAccount.id);
      const cleanedSelected = internalSelectedPlatforms.filter(id => validIds.includes(id));

      if (cleanedSelected.length !== internalSelectedPlatforms.length) {
        updateLocalStorage({ selectedPlatforms: cleanedSelected });
      }
    }
  }, [activeProject, internalSelectedPlatforms, updateLocalStorage, selectedIds]);

  const sourceAccounts: Account[] = (accounts || activeProject?.socialAccounts?.map(sa => ({
    id: sa.socialAccount.id,
    name: sa.socialAccount.name,
    platform: sa.socialAccount.platform,
    avatarUrl: sa.socialAccount.avatarUrl,
    isValid: sa.socialAccount.isValid !== false,
    handle: sa.socialAccount.handle
  })) || []) as Account[];

  const [localOrder, setLocalOrder] = useState<string[]>([]);

  useEffect(() => {
    setLocalOrder(sourceAccounts.map(a => a.id));
  }, [sourceAccounts.map(a => a.id).join(',')]);

  const displayAccounts = localOrder
    .map(id => sourceAccounts.find(a => a.id === id))
    .filter(Boolean) as Account[];

  const handleItemClick = (accountId: string) => {
    const account = displayAccounts.find(a => a.id === accountId);

    // If connection is broken and we are in the selector, prioritize reconnection
    if (account && account.isValid === false && mode !== 'select') {
      setReconnectPlatform(account.platform);
      setIsConnectModalOpen(true);
      return;
    }

    if (onToggle) {
      onToggle(accountId);
      return;
    }

    const isSelected = currentSelectedIds.includes(accountId);
    const newSelected = isSelected
      ? currentSelectedIds.filter(id => id !== accountId)
      : [...currentSelectedIds, accountId];

    updateLocalStorage({ selectedPlatforms: newSelected });
  };

  const isSmall = displayAccounts.length > 4;
  const iconSize = isSmall ? "h-7 w-7" : "h-10 w-10";
  const badgeSize = isSmall ? "h-4 w-4" : "h-6 w-6";
  const badgeIconSize = isSmall ? "h-2 w-2" : "h-3.5 w-3.5";

  const [isConnectModalOpen, setIsConnectModalOpen] = React.useState(false);
  const [reconnectPlatform, setReconnectPlatform] = React.useState<string | undefined>(undefined);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localOrder.indexOf(active.id as string);
    const newIndex = localOrder.indexOf(over.id as string);
    const reordered = arrayMove(localOrder, oldIndex, newIndex);
    setLocalOrder(reordered);

    try {
      await axios.put(`${API_BASE_URL}/social-accounts/reorder`, {
        orderedIds: reordered
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      refreshProjects();
    } catch (err) {
      // Revert on error
      setLocalOrder(sourceAccounts.map(a => a.id));
    }
  };

  return (
    <TooltipProvider delayDuration={0}>
      <div className={cn(
        "bg-card border border-border rounded-2xl flex flex-col items-center pt-5 pb-4 gap-4 h-full shadow-xl relative overflow-hidden transition-all duration-300",
        isSmall ? "w-[56px]" : "w-[72px]",
        className
      )}>
        <div className="flex flex-col items-center gap-1 shrink-0">
          <span className="text-[9px] font-black text-muted-foreground uppercase tracking-widest">
            {mode === 'select' ? 'Select' : 'Active'}
          </span>
          <div className="h-1 w-4 bg-primary/20 rounded-full" />
        </div>

        <ScrollArea className="w-full flex-1">
          <div className={cn(
            "flex flex-col items-center px-3 py-4",
            isSmall ? "gap-4" : "gap-6"
          )}>
            {showAggregateButton && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className="relative group cursor-pointer"
                    onClick={onAggregateClick}
                  >
                    <div className={cn(
                      "rounded-full flex items-center justify-center transition-all duration-300 transform group-hover:scale-110",
                      iconSize,
                      isAggregateSelected
                        ? "bg-primary text-white shadow-lg shadow-primary/20 ring-2 ring-offset-2 ring-offset-[#1e293b] ring-primary"
                        : "bg-card text-muted-foreground border border-border"
                    )}>
                      <BarChart3 className={isSmall ? "h-3.5 w-3.5" : "h-5 w-5"} />
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="right" className="bg-background border-border text-foreground">
                  <p className="font-bold">Aggregate Engagement</p>
                  <p className="text-[10px] text-muted-foreground">View combined stats</p>
                </TooltipContent>
              </Tooltip>
            )}

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
              <SortableContext items={localOrder} strategy={verticalListSortingStrategy}>
                {displayAccounts.map((account) => (
                  <SortableAccount
                    key={account.id}
                    account={account}
                    isSelected={currentSelectedIds.includes(account.id)}
                    isSmall={isSmall}
                    iconSize={iconSize}
                    badgeSize={badgeSize}
                    badgeIconSize={badgeIconSize}
                    onClick={() => handleItemClick(account.id)}
                    isDisabled={disabledAccountIds?.includes(account.id)}
                    disabledReason={disabledReason}
                  />
                ))}
              </SortableContext>
            </DndContext>

            {showConnectButton && (
              <>
                <Separator className="bg-card w-8 my-1" />

                <Tooltip>
                  <TooltipTrigger asChild>
                <div
                  className="relative group cursor-pointer"
                  onClick={() => setIsConnectModalOpen(true)}
                >
                  <div className={cn(
                    "rounded-full border border-dashed border-border flex items-center justify-center text-muted-foreground transition-all duration-300 group-hover:border-solid group-hover:bg-primary/10 group-hover:scale-110 group-hover:text-primary group-hover:border-primary/50",
                    iconSize
                  )}>
                    <Plus className={isSmall ? "h-3.5 w-3.5" : "h-5 w-5"} strokeWidth={3} />

                    <div className={cn(
                      "absolute -bottom-1 -right-1 bg-card rounded-full border border-border p-0.5 shadow-lg group-hover:bg-primary group-hover:border-primary group-hover:text-white transition-colors",
                      isSmall ? "h-3.5 w-3.5" : "h-5 w-5"
                    )}>
                      <Share2 className={isSmall ? "h-2 w-2" : "h-3 w-3"} />
                    </div>
                  </div>
                </div>
                  </TooltipTrigger>
                  <TooltipContent side="right" className="bg-background border-border text-foreground">
                    <p className="font-bold">Connect Account</p>
                    <p className="text-[10px] text-muted-foreground">Add a new social network</p>
                  </TooltipContent>
                </Tooltip>

                <SocialConnectionModal
                  open={isConnectModalOpen}
                  onOpenChange={(open) => {
                    setIsConnectModalOpen(open);
                    if (!open) setReconnectPlatform(undefined);
                  }}
                  initialPlatform={reconnectPlatform}
                />
              </>
            )}

            {showPresetsButton && mode === 'toggle' && (
              <PresetsPopover isSmall={isSmall} iconSize={iconSize} />
            )}
          </div>
        </ScrollArea>
      </div>
    </TooltipProvider>
  );
}

// -- Presets Popover component --
function PresetsPopover({ isSmall, iconSize }: { isSmall: boolean; iconSize: string }) {
  const { presets, applyPreset, saveCurrentAsPreset, deletePreset, renamePreset, canSaveMore } = usePresets();
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const editInputRef = useRef<HTMLInputElement>(null);

  const handleCreate = () => {
    if (newName.trim()) {
      saveCurrentAsPreset(newName.trim());
      setNewName('');
      setIsCreating(false);
    }
  };

  const handleApply = (presetId: string) => {
    applyPreset(presetId);
    setIsOpen(false);
  };

  const handleStartEdit = (presetId: string, currentName: string) => {
    setEditingId(presetId);
    setEditName(currentName);
    setTimeout(() => editInputRef.current?.focus(), 50);
  };

  const handleFinishEdit = () => {
    if (editingId && editName.trim()) {
      renamePreset(editingId, editName.trim());
    }
    setEditingId(null);
    setEditName('');
  };

  return (
    <>
      <Separator className="bg-card w-8 my-1" />
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <div className="relative group cursor-pointer">
                <div className={cn(
                  "rounded-full border border-dashed border-border flex items-center justify-center text-muted-foreground transition-all duration-300 group-hover:border-solid group-hover:bg-amber-500/10 group-hover:scale-110 group-hover:text-amber-500 group-hover:border-amber-500/50",
                  isOpen && "border-solid bg-amber-500/10 text-amber-500 border-amber-500/50 scale-110",
                  iconSize
                )}>
                  <Bookmark className={isSmall ? "h-3.5 w-3.5" : "h-4 w-4"} strokeWidth={2.5} />
                </div>
              </div>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent side="right" className="bg-background border-border text-foreground">
            <p className="font-bold">Presets</p>
            <p className="text-[10px] text-muted-foreground">Save & load platform selections</p>
          </TooltipContent>
        </Tooltip>

        <PopoverContent side="right" align="start" className="w-56 p-0 bg-card border-border">
          <div className="p-3 pb-2">
            <p className="text-xs font-bold text-foreground">Presets</p>
            <p className="text-[10px] text-muted-foreground">Click to apply a saved selection</p>
          </div>
          <Separator className="bg-border" />

          <div className="max-h-60 overflow-y-auto">
            {presets.length === 0 && !isCreating && (
              <div className="px-3 py-4 text-center">
                <Bookmark className="h-5 w-5 text-muted-foreground mx-auto mb-1.5 opacity-40" />
                <p className="text-[10px] text-muted-foreground">No presets yet</p>
                <p className="text-[9px] text-muted-foreground/60">Save your current selection below</p>
              </div>
            )}

            {presets.map((preset) => (
              <div
                key={preset.id}
                className="group/item flex items-center gap-2 px-3 py-2 hover:bg-muted/50 transition-colors"
              >
                <Star className="h-3 w-3 text-amber-500 shrink-0" />

                {editingId === preset.id ? (
                  <input
                    ref={editInputRef}
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onBlur={handleFinishEdit}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleFinishEdit();
                      if (e.key === 'Escape') { setEditingId(null); setEditName(''); }
                    }}
                    className="flex-1 text-xs bg-muted/50 border border-border rounded px-1.5 py-0.5 text-foreground outline-none focus:border-amber-500/50"
                    maxLength={50}
                  />
                ) : (
                  <button
                    onClick={() => handleApply(preset.id)}
                    className="flex-1 text-left text-xs text-foreground truncate hover:text-amber-500 transition-colors"
                    title={preset.name}
                  >
                    {preset.name}
                  </button>
                )}

                <div className="flex items-center gap-0.5 opacity-0 group-hover/item:opacity-100 transition-opacity shrink-0">
                  {editingId !== preset.id && (
                    <button
                      onClick={(e) => { e.stopPropagation(); handleStartEdit(preset.id, preset.name); }}
                      className="p-0.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                      title="Rename"
                    >
                      <Pencil className="h-2.5 w-2.5" />
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); deletePreset(preset.id); }}
                    className="p-0.5 rounded hover:bg-red-500/10 text-muted-foreground hover:text-red-400 transition-colors"
                    title="Delete"
                  >
                    <X className="h-2.5 w-2.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>

          {canSaveMore && (
            <>
              <Separator className="bg-border" />
              <div className="p-2">
                {isCreating ? (
                  <div className="flex items-center gap-1.5">
                    <input
                      ref={inputRef}
                      value={newName}
                      onChange={(e) => setNewName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleCreate();
                        if (e.key === 'Escape') { setIsCreating(false); setNewName(''); }
                      }}
                      placeholder="Preset name..."
                      className="flex-1 text-xs bg-muted/50 border border-border rounded px-2 py-1 text-foreground placeholder:text-muted-foreground outline-none focus:border-amber-500/50"
                      maxLength={50}
                      autoFocus
                    />
                    <button
                      onClick={handleCreate}
                      disabled={!newName.trim()}
                      className="p-1 rounded bg-amber-500 text-white disabled:opacity-30 hover:bg-amber-600 transition-colors"
                    >
                      <Check className="h-3 w-3" />
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => { setIsCreating(true); setTimeout(() => inputRef.current?.focus(), 50); }}
                    className="flex items-center gap-1.5 text-[11px] text-muted-foreground hover:text-amber-500 transition-colors w-full px-1 py-0.5"
                  >
                    <PlusCircle className="h-3.5 w-3.5" />
                    Save current selection
                  </button>
                )}
              </div>
            </>
          )}
        </PopoverContent>
      </Popover>
    </>
  );
}
